import { useEffect, useState } from 'react';
import { Icon } from '../ds/index.js';

/** Fréquence de vérification d'une nouvelle version (et au retour sur l'onglet). */
const CHECK_INTERVAL_MS = 5 * 60_000;

/** Script principal de la page (`/assets/index-<empreinte>.js`), null en dev (Vite). */
function currentBundle(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  return script ? new URL(script.src, location.href).pathname : null;
}

/** Script principal de la version en ligne (index.html n'est jamais mis en cache, nginx.web.conf). */
async function deployedBundle(): Promise<string | null> {
  const res = await fetch('/index.html', { cache: 'no-store' });
  if (!res.ok) return null;
  return (await res.text()).match(/\/assets\/index-[A-Za-z0-9_-]+\.js/)?.[0] ?? null;
}

/**
 * Bandeau « nouvelle version » : après un déploiement, une page déjà ouverte
 * garde l'ancien code jusqu'au rechargement (un changement de carte ou une
 * reconnexion ne recharge pas). Vu le 2026-10-28 : un ordre du serveur que
 * l'ancien code ignorait (« Aucun handler pour moderation:media-off »).
 * Jamais de rechargement d'office : il couperait une réunion. « Plus tard »
 * masque le bandeau jusqu'à la version suivante.
 */
export function UpdateBanner() {
  const [available, setAvailable] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);

  useEffect(() => {
    const mine = currentBundle();
    if (!mine) return; // dev : pas de script empreinté
    let stopped = false;
    const check = () => {
      if (document.hidden) return;
      deployedBundle()
        .then((online) => { if (!stopped && online && online !== mine) setAvailable(online); })
        .catch(() => { /* réseau coupé : nouvel essai plus tard */ });
    };
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    check();
    return () => { stopped = true; clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  if (!available || dismissed === available) return null;
  return (
    <div role="status" aria-live="polite" className="pl-hud pl-connection-banner">
      <span className="pl-connection-banner-text">
        <Icon name="refresh" size={16} />
        Une nouvelle version de Placio est disponible.
      </span>
      <span className="pl-hud-sep" />
      <button type="button" className="pl-hud-btn" onClick={() => window.location.reload()}>
        Recharger
      </button>
      <button type="button" className="pl-hud-btn" onClick={() => setDismissed(available)}>
        Plus tard
      </button>
    </div>
  );
}
